import { useMemo, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "~/atoms/checkbox";
import { Spinner } from "~/atoms/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { type Option } from "~/molecules/ControlledCombobox";
import { ComboboxCell } from "~/molecules/onboard-documents/ComboboxCell";
import { hiddenNativeScrollbarClass } from "~/molecules/StickyHorizontalScrollbar";
import { StatusBadge } from "~/molecules/StatusBadge";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { printFullName } from "~/helpers/user";
import { getTierTitle } from "~/helpers/tier";
import {
  type DocumentVersionSummary,
  type ScheduleReviewPeriod,
} from "~/api/types";
import {
  DocumentVersionStatus,
  reviewPeriod,
  type ReviewPeriod,
} from "~/config/types";
import { getReviewPeriodLabel } from "~/helpers/document";

const NO_REVIEW_PERIOD_VALUE = "__no_review_period__";

type CompleteRowHoverCol = "reviewPeriod" | "status";

const reviewPeriodToComboboxValue = (period: ScheduleReviewPeriod) =>
  period ?? NO_REVIEW_PERIOD_VALUE;

const comboboxValueToReviewPeriod = (value: string): ScheduleReviewPeriod =>
  value === NO_REVIEW_PERIOD_VALUE ? null : (value as ReviewPeriod);

interface Props {
  readOnly: boolean;
  wrapperRef?: Ref<HTMLDivElement>;
  rows: DocumentVersionSummary[];
  selectedIds: Set<number>;
  allSelected: boolean;
  publishingIds: Set<number>;
  statusById: Map<number, DocumentVersionStatus>;
  reviewPeriodById: Map<number, ScheduleReviewPeriod>;
  onToggleAll: () => void;
  onToggleRow: (id: number) => void;
  onUnselectAll: () => void;
  onStatusChange: (
    id: number,
    status: DocumentVersionStatus,
    opts?: { bulk?: boolean },
  ) => void;
  onReviewPeriodChange: (
    id: number,
    period: ScheduleReviewPeriod,
    opts?: { bulk?: boolean },
  ) => void;
}

export const CompleteTable = ({
  readOnly,
  wrapperRef,
  rows,
  selectedIds,
  allSelected,
  publishingIds,
  statusById,
  reviewPeriodById,
  onToggleAll,
  onToggleRow,
  onUnselectAll,
  onStatusChange,
  onReviewPeriodChange,
}: Props) => {
  const { t } = useTranslation();
  const { onHover, onHoverLeave, isHighlighted } = useBulkHoverHighlight<
    number,
    CompleteRowHoverCol
  >(selectedIds);

  const statusOptions = useMemo<Option[]>(
    () => [
      {
        value: DocumentVersionStatus.published,
        label: t(($) => $.commons.document.statuses.published),
      },
      {
        value: DocumentVersionStatus.draft,
        label: t(($) => $.commons.document.statuses.draft),
      },
    ],
    [t],
  );

  const reviewPeriodOptions = useMemo<Option[]>(
    () => [
      ...reviewPeriod.map((value) => ({
        value,
        label: getReviewPeriodLabel(value, t) ?? value,
      })),
      {
        value: `${NO_REVIEW_PERIOD_VALUE}_separator`,
        label: "",
        isSeparator: true,
      },
      {
        value: NO_REVIEW_PERIOD_VALUE,
        label: t(($) => $.commons.document.noRecurringReview),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-1">
      <BulkSelectionBar
        count={selectedIds.size}
        onUnselectAll={onUnselectAll}
      />
      <Table
        wrapperRef={wrapperRef}
        wrapperClassName={`rounded-md border ${hiddenNativeScrollbarClass}`}
      >
        <TableHeader className="bg-muted">
          <TableRow>
            <TableHead>
              <Checkbox
                checked={allSelected}
                onCheckedChange={onToggleAll}
                aria-label={t(($) => $.commons.buttons.selectAll)}
              />
            </TableHead>
            <TableHead>{t(($) => $.commons.document.title)}</TableHead>
            <TableHead>{t(($) => $.commons.document.tier)}</TableHead>
            <TableHead>{t(($) => $.commons.document.category)}</TableHead>
            <TableHead>{t(($) => $.commons.document.owner)}</TableHead>
            <TableHead>{t(($) => $.commons.document.publisher)}</TableHead>
            <TableHead>
              {t(($) => $.commons.document.recurringReview)}
            </TableHead>
            <TableHead>{t(($) => $.commons.document.status)}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <CompleteRow
              readOnly={readOnly}
              key={row.id}
              row={row}
              isSelected={selectedIds.has(row.id)}
              isPublishing={publishingIds.has(row.id)}
              status={statusById.get(row.id) ?? DocumentVersionStatus.published}
              statusOptions={statusOptions}
              reviewPeriod={reviewPeriodById.get(row.id) ?? null}
              reviewPeriodOptions={reviewPeriodOptions}
              reviewPeriodHighlighted={isHighlighted(row.id, "reviewPeriod")}
              statusHighlighted={isHighlighted(row.id, "status")}
              onHoverReviewPeriod={() => onHover(row.id, "reviewPeriod")}
              onHoverStatus={() => onHover(row.id, "status")}
              onHoverLeave={onHoverLeave}
              onToggle={() => onToggleRow(row.id)}
              onReviewPeriodChange={(period) =>
                onReviewPeriodChange(row.id, period, { bulk: true })
              }
              onStatusChange={(status) =>
                onStatusChange(row.id, status, { bulk: true })
              }
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

interface CompleteRowProps {
  readOnly: boolean;
  row: DocumentVersionSummary;
  isSelected: boolean;
  isPublishing: boolean;
  status: DocumentVersionStatus;
  statusOptions: Option[];
  reviewPeriod: ScheduleReviewPeriod;
  reviewPeriodOptions: Option[];
  reviewPeriodHighlighted: boolean;
  statusHighlighted: boolean;
  onHoverReviewPeriod: () => void;
  onHoverStatus: () => void;
  onHoverLeave: () => void;
  onToggle: () => void;
  onReviewPeriodChange: (period: ScheduleReviewPeriod) => void;
  onStatusChange: (status: DocumentVersionStatus) => void;
}

const CompleteRow = ({
  readOnly,
  row,
  isSelected,
  isPublishing,
  status,
  statusOptions,
  reviewPeriod,
  reviewPeriodOptions,
  reviewPeriodHighlighted,
  statusHighlighted,
  onHoverReviewPeriod,
  onHoverStatus,
  onHoverLeave,
  onToggle,
  onReviewPeriodChange,
  onStatusChange,
}: CompleteRowProps) => {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: row.title,
          })}
        />
      </TableCell>
      <TableCell>{row.title}</TableCell>
      <TableCell className="min-w-fit">
        {getTierTitle(row.document.tier?.title, t)}
      </TableCell>
      <TableCell className="min-w-fit">{row.document.category.title}</TableCell>
      <TableCell className="min-w-fit">
        {printFullName(row.document.owner)}
      </TableCell>
      <TableCell className="min-w-fit">
        {printFullName(row.document.publisher)}
      </TableCell>
      <TableCell className="w-52">
        {readOnly ? (
          (getReviewPeriodLabel(reviewPeriod, t) ?? "-")
        ) : (
          <ComboboxCell
            value={reviewPeriodToComboboxValue(reviewPeriod)}
            onChange={(value) =>
              onReviewPeriodChange(comboboxValueToReviewPeriod(value))
            }
            options={reviewPeriodOptions}
            highlighted={reviewPeriodHighlighted}
            onHover={onHoverReviewPeriod}
            onHoverLeave={onHoverLeave}
          />
        )}
      </TableCell>
      <TableCell className="w-44">
        {isPublishing ? (
          <div className="flex h-8 items-center justify-center">
            <Spinner className="size-4" />
          </div>
        ) : readOnly ? (
          <StatusBadge.DocumentVersionStatus status={row.status} />
        ) : (
          <ComboboxCell
            value={status}
            onChange={(value) => onStatusChange(value as DocumentVersionStatus)}
            options={statusOptions}
            highlighted={statusHighlighted}
            onHover={onHoverStatus}
            onHoverLeave={onHoverLeave}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
